import { escapeAttr, escapeHtml } from "@emkit/builder-core";
import { looksLikeHtml, sanitizeRichHtml } from "./sanitize.js";
export const FAQ_DEFAULTS = {
    items: [
        {
            id: "q1",
            question: "How does pricing work?",
            answer: "Pick a plan and pay monthly — no long-term contracts. Upgrade or downgrade at any time.",
        },
        {
            id: "q2",
            question: "Can I host the output anywhere?",
            answer: "Yes. Pages compile to static HTML + CSS. Drop them on any host: Cloudflare Pages, Netlify, S3, a plain nginx box.",
        },
        {
            id: "q3",
            question: "Does EmBuilder need a backend?",
            answer: "Only if you use the Form widget with a custom `action`. Every other widget renders without JavaScript.",
        },
        {
            id: "q4",
            question: "Can I import my existing pages?",
            answer: "Import JSON round-trips with Export JSON — if a page was built in EmBuilder you can re-open it. Raw HTML import is on the roadmap.",
        },
    ],
    title: "Frequently asked questions",
    subtitle: "",
    exclusive: true,
    defaultOpenFirst: false,
    emitSchemaOrg: true,
    layout: "single",
    background: "transparent",
    textColor: "#0f172a",
};
function stripHtml(s) {
    // Cheap text extraction for JSON-LD — sanitizer output is still HTML,
    // and schema.org FAQPage answers should be plain text / HTML string.
    return s.replace(/<[^>]*>/g, "").trim();
}
export const faqWidget = {
    type: "faq",
    defaultProps: FAQ_DEFAULTS,
    renderHtml(props) {
        const items = props.items ?? [];
        if (items.length === 0) {
            return `<div class="emk-faq" style="padding:12px;color:#9ca3af;font-size:13px;text-align:center;border:1px dashed #e5e7eb;border-radius:6px">(Empty FAQ — add questions in the inspector)</div>`;
        }
        const exclusive = props.exclusive ?? true;
        const defaultOpenFirst = props.defaultOpenFirst ?? false;
        const emitSchema = props.emitSchemaOrg ?? true;
        const layout = props.layout ?? "single";
        const bg = props.background ?? "transparent";
        const text = props.textColor ?? "#0f172a";
        const groupName = `faq-${Math.random().toString(36).slice(2, 8)}`;
        const nameAttr = exclusive ? ` name="${escapeAttr(groupName)}"` : "";
        const rows = items
            .map((item, i) => {
            const open = defaultOpenFirst && i === 0 ? " open" : "";
            const body = looksLikeHtml(item.answer)
                ? sanitizeRichHtml(item.answer)
                : `<p style="margin:0">${escapeHtml(item.answer).replace(/\n/g, "<br>")}</p>`;
            return `<details class="emk-faq-item"${nameAttr}${open} style="border-bottom:1px solid rgba(0,0,0,0.08)"><summary style="list-style:none;cursor:pointer;padding:16px 0;font-weight:500;font-size:17px;display:flex;justify-content:space-between;align-items:center;gap:16px;color:${text}"><span>${escapeHtml(item.question)}</span><span aria-hidden="true" style="color:#9ca3af;font-size:20px;line-height:1">+</span></summary><div class="emk-faq-body" style="padding:0 0 16px;color:${text};opacity:0.8;line-height:1.6;font-size:15px">${body}</div></details>`;
        })
            .join("");
        const schema = emitSchema
            ? `<script type="application/ld+json">${JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: items.map((it) => ({
                    "@type": "Question",
                    name: it.question,
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: stripHtml(it.answer),
                    },
                })),
            }).replace(/</g, "\\u003c")}</script>`
            : "";
        const heading = props.title
            ? `<div class="emk-faq-heading" style="text-align:center;margin-bottom:32px"><h2 style="margin:0;font-size:30px;font-weight:700;letter-spacing:-0.01em;color:${text}">${escapeHtml(props.title)}</h2>${props.subtitle ? `<p style="margin:8px 0 0;color:${text};opacity:0.7;font-size:16px;line-height:1.55">${escapeHtml(props.subtitle)}</p>` : ""}</div>`
            : "";
        if (layout === "two-column") {
            // Simple CSS columns keep zero-JS; on narrow screens it collapses to 1.
            const inner = `<div style="column-count:2;column-gap:40px"><div style="break-inside:avoid">${rows}</div></div>`;
            return `<div class="emk-faq" style="background:${bg};padding:20px 0;font-family:inherit"><div style="max-width:1000px;margin:0 auto">${heading}${inner}</div>${schema}</div>`;
        }
        return `<div class="emk-faq" style="background:${bg};padding:20px 0;font-family:inherit"><div style="max-width:720px;margin:0 auto">${heading}${rows}</div>${schema}</div>`;
    },
};
//# sourceMappingURL=faq.js.map